import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const CreateWorkoutPlanModal = ({ onClose, onCreate, exercises }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    difficulty: 'beginner',
    durationMinutes: '',
    tags: ['']
  });
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);

    const planData = {
      ...formData,
      durationMinutes: parseInt(formData?.durationMinutes),
      tags: formData?.tags?.filter(t => t?.trim()),
      isTemplate: true
    };

    await onCreate(planData, selectedExercises);
    setIsLoading(false);
  };

  const addExercise = (exercise) => {
    setSelectedExercises(prev => [
      ...prev,
      {
        exerciseId: exercise?.id,
        name: exercise?.name,
        sets: 3,
        reps: 10,
        durationSeconds: null,
        restSeconds: 60
      }
    ]);
  };

  const updateExercise = (index, field, value) => {
    const updated = [...selectedExercises];
    updated[index][field] = value;
    setSelectedExercises(updated);
  };

  const removeExercise = (index) => {
    setSelectedExercises(prev => prev?.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-foreground">Create Workout Plan</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Plan Name"
            value={formData?.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Difficulty</label>
              <select
                value={formData?.difficulty}
                onChange={(e) => setFormData(prev => ({ ...prev, difficulty: e?.target?.value }))}
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <Input
              label="Duration (minutes)"
              type="number"
              value={formData?.durationMinutes}
              onChange={(e) => setFormData(prev => ({ ...prev, durationMinutes: e?.target?.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Add Exercises</label>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-border rounded-lg">
              {exercises?.map((exercise) => (
                <button
                  key={exercise?.id}
                  type="button"
                  onClick={() => addExercise(exercise)}
                  className="text-left px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm"
                  disabled={selectedExercises?.some(e => e?.exerciseId === exercise?.id)}
                >
                  {exercise?.name}
                </button>
              ))}
            </div>
          </div>

          {selectedExercises?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Selected Exercises</label>
              <div className="space-y-3">
                {selectedExercises?.map((ex, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <span className="flex-1 font-medium">{ex?.name}</span>
                    <Input
                      type="number"
                      placeholder="Sets"
                      value={ex?.sets}
                      onChange={(e) => updateExercise(idx, 'sets', parseInt(e?.target?.value))}
                      className="w-20"
                    />
                    <Input
                      type="number"
                      placeholder="Reps"
                      value={ex?.reps}
                      onChange={(e) => updateExercise(idx, 'reps', parseInt(e?.target?.value))}
                      className="w-20"
                    />
                    <button
                      type="button"
                      onClick={() => removeExercise(idx)}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <Icon name="Trash2" size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="submit" loading={isLoading} fullWidth disabled={selectedExercises?.length === 0}>
              Create Workout Plan
            </Button>
            <Button type="button" variant="outline" onClick={onClose} fullWidth>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkoutPlanModal;